import React, { useEffect, useState } from "react"
import { Table, Tag, Button, Card, Typography, Space, Tooltip, Input, Modal, message, List, InputNumber } from "antd"
import { SearchOutlined, WarningOutlined, CheckCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { useCompany } from "../../../contexts/CompanyContext"
import { useConfig } from "../../../contexts/ConfigContext"
import { useUser } from "../../../contexts/UserContext"
import { useSecureRequest } from "../../../hooks/useSecureRequest"
import useAuth from "../../../hooks/useAuth"
import { useHistory } from "react-router-dom"

const { Title, Text, Paragraph } = Typography

export function ViewStockOverrideList() {
  const history = useHistory()
  const { config } = useConfig()
  const { company } = useCompany()
  const { user } = useUser()
  const [loading, setLoading] = useState(true)
  const [settleLoading, setSettleLoading] = useState<string | null>(null)
  const [movements, setMovements] = useState<any[]>([])
  const [searchText, setSearchText] = useState("")

  // Modal State for Guided Adjustment Flow
  const [deficitModalVisible, setDeficitModalVisible] = useState(false)
  const [selectedMovement, setSelectedMovement] = useState<any>(null)
  const [deficitItems, setDeficitItems] = useState<any[]>([])
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({})
  const [adjustmentNotes, setAdjustmentNotes] = useState("")
  const [adjustmentLoading, setAdjustmentLoading] = useState(false)

  const { securePost } = useSecureRequest()
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key)

  useEffect(() => {
    fetchOverrideMovements()
  }, [company.current?.uid, tokenData])

  const fetchOverrideMovements = async () => {
    if (!company.current?.uid) return
    setLoading(true)
    try {
      const response = await securePost("/inventory/movement/get-stock-override-movements", {
        company: company.current.uid
      })
      if (response.status === 200 && response.movementList) {
        setMovements(response.movementList)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSettle = async (record: any) => {
    if (!company.current?.uid || !record?.uid) return
    setSettleLoading(record.uid)

    try {
      const response = await securePost("/inventory/movement/settle-stock-override-movement", {
        company: company.current.uid,
        uidMovement: record.uid
      })

      if (response.hasDeficit || response.status === 220 || (response.deficitItems && response.deficitItems.length > 0)) {
        setSelectedMovement(record)
        const items = response.deficitItems || []
        setDeficitItems(items)

        const initialQtyMap: Record<number, number> = {}
        items.forEach((item: any) => {
          initialQtyMap[item.element_id] = item.deficit || 1
        })
        setItemQuantities(initialQtyMap)
        setAdjustmentNotes(`Ajuste de regularización para documento ${record.document_number || record.uid}`)
        setDeficitModalVisible(true)
      } else if (response.status === 200) {
        message.success(response.message || "Movimiento regularizado exitosamente")
        setDeficitModalVisible(false)
        fetchOverrideMovements()
      } else {
        message.error(response.message || "No fue posible regularizar el movimiento")
      }
    } catch (err) {
      console.error(err)
      message.error("Error de conexión al regularizar movimiento")
    } finally {
      setSettleLoading(null)
    }
  }

  const handleRegisterAdjustment = async () => {
    if (!company.current?.uid || !selectedMovement) return
    setAdjustmentLoading(true)
    try {
      const storehouseId = selectedMovement.storehouse_from_id || selectedMovement.storehouse_to_id
      const adjDocNumber = `AJUS-${Date.now().toString().slice(-6)}`
      
      const adjItems = deficitItems.map(d => {
        const qty = itemQuantities[d.element_id] || d.deficit
        const price = d.unit_price || 0
        return {
          id: d.element_id,
          element_id: d.element_id,
          uid: d.uid || "",
          item_code: d.item_code || "",
          item_name: d.item_name || "",
          item_description: d.item_description || d.item_name || "",
          display: d.presentation || "UND",
          presentation: d.presentation || "UND",
          quantity: qty,
          unit_price: price,
          total_tax_amount: 0,
          discount_amount: 0,
          total_amount: qty * price
        }
      })

      const userName = user?.name || "Sistema"
      const userDoc = user?.document_number || ""

      const createResponse = await securePost("/inventory/movement/create-movement", {
        company: company.current.uid,
        movement: {
          document_number: adjDocNumber,
          document_type: "ADJUSTMENT",
          invoice_type: "SIMPLE_RECEIPT",
          status: "COMPLETED",
          storehouse_to_id: storehouseId,
          contact_id: null,
          contact_name: "AJUSTE DE INVENTARIO",
          created_by_user_id: user?.id || null,
          created_by_user_name: userName,
          created_by_user_document: userDoc,
          approved_by_user_id: user?.id || null,
          approved_by_user_name: userName,
          approved_by_user_document: userDoc,
          notes: adjustmentNotes || `Ajuste de regularización para documento ${selectedMovement.document_number}`,
          item_list: adjItems
        }
      })

      if (createResponse.status === 200) {
        // Settle original document — force clear stock_override without deficit check
        await securePost("/inventory/movement/settle-stock-override-movement", {
          company: company.current.uid,
          uidMovement: selectedMovement.uid,
          forceSettle: true
        })
        message.success("Ajuste de inventario registrado y documento regularizado exitosamente")
        setDeficitModalVisible(false)
        fetchOverrideMovements()
      } else {
        message.error(createResponse.message || "Error al registrar el movimiento de ajuste")
      }
    } catch (err) {
      console.error(err)
      message.error("Error al procesar el ajuste de inventario")
    } finally {
      setAdjustmentLoading(false)
    }
  }

  const handleQuantityChange = (elementId: number, val: number | null) => {
    if (val !== null && val >= 1) {
      setItemQuantities(prev => ({ ...prev, [elementId]: val }))
    }
  }

  const columns = [
    {
      title: "Documento",
      dataIndex: "document_number",
      key: "document_number",
      width: 140,
      render: (text: string, record: any) => (
        <a onClick={() => history.push(`/movement/${record.uid || record.id}`)} style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {text || record.uid}
        </a>
      )
    },
    {
      title: "Tipo",
      dataIndex: "document_type",
      key: "document_type",
      width: 110,
      render: (type: string) => {
        const typesMap: Record<string, string> = {
          SALE_INVOICE: "Venta",
          TRANSFER: "Traslado",
          CONVERSION: "Fraccionamiento",
          PRODUCTION_EXIT: "Consumo Fabricación",
          ADJUSTMENT: "Ajuste",
          WASTE: "Merma"
        }
        return <Tag color="blue" style={{ borderRadius: 4 }}>{typesMap[type] || type}</Tag>
      }
    },
    {
      title: "Artículos Afectados",
      key: "affected_items",
      render: (_: any, record: any) => {
        const itemsWithOverride = record.item_list?.filter((i: any) => i.stock_override === true) || record.item_list || []
        return (
          <Space direction="vertical" size={2}>
            {itemsWithOverride.map((item: any, idx: number) => (
              <Text key={idx} type="secondary" style={{ fontSize: 13, display: "block" }}>
                • {item.item_name || item.name} <strong>(Cant: {item.quantity})</strong>
              </Text>
            ))}
          </Space>
        )
      }
    },
    {
      title: "Motivo de Autorización",
      dataIndex: "stock_override_reason",
      key: "stock_override_reason",
      ellipsis: true,
      render: (reason: string) => reason ? (
        <Tooltip title={reason}>
          <Text style={{ maxWidth: 220 }} ellipsis>{reason}</Text>
        </Tooltip>
      ) : <Text type="secondary">—</Text>
    },
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (date: string) => (
        <span style={{ whiteSpace: "nowrap", fontSize: 13 }}>
          {date ? new Date(date).toLocaleDateString("es-CO", {
            year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
          }) : "—"}
        </span>
      )
    },
    {
      title: "Estado",
      key: "status",
      width: 80,
      align: "center" as const,
      render: () => (
        <Tooltip title="Pendiente de Ajuste de Inventario">
          <Tag
            icon={<WarningOutlined style={{ marginRight: 0 }} />}
            color="warning"
            style={{ borderRadius: 12, padding: "2px 8px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          />
        </Tooltip>
      )
    },
    {
      title: "Acciones",
      key: "actions",
      width: 90,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Tooltip title="Regularizar Stock">
          <Button
            type="primary"
            shape="circle"
            icon={<CheckCircleOutlined />}
            loading={settleLoading === record.uid}
            onClick={() => handleSettle(record)}
          />
        </Tooltip>
      )
    }
  ]

  const filteredMovements = movements.filter((m: any) => {
    if (!searchText) return true
    const term = searchText.toLowerCase()
    return (
      m.document_number?.toLowerCase().includes(term) ||
      m.stock_override_reason?.toLowerCase().includes(term) ||
      m.item_list?.some((i: any) => i.item_name?.toLowerCase().includes(term) || i.name?.toLowerCase().includes(term))
    )
  })

  return (
    <div style={{ padding: "16px 24px" }}>
      <Card style={{ borderRadius: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
              Movimientos Pendientes de Ajuste de Inventario
            </Title>
            <Text type="secondary">
              Listado de salidas autorizadas excepcionalmente sin stock registrado. Requieren abastecimiento o ajuste.
            </Text>
          </div>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Buscar por N° documento, producto o motivo..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 320, maxWidth: "100%" }}
            allowClear
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredMovements}
          rowKey={(r) => r.id || r.uid}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "No hay movimientos pendientes de ajuste de inventario" }}
        />
      </Card>

      {/* Guided Adjustment Form Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: "#faad14" }} />
            <span>Regularización de Inventario: Crear Ajuste de Entrada</span>
          </Space>
        }
        visible={deficitModalVisible}
        onCancel={() => setDeficitModalVisible(false)}
        width={560}
        footer={[
          <Button key="cancel" onClick={() => setDeficitModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="adjust"
            type="primary"
            icon={<PlusCircleOutlined />}
            loading={adjustmentLoading}
            onClick={handleRegisterAdjustment}
          >
            Registrar Ajuste de Entrada y Regularizar
          </Button>
        ]}
      >
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
          Complete los valores para generar el registro de ingreso por ajuste que regularizará el documento <strong>{selectedMovement?.document_number}</strong>. Las cantidades vienen pre-llenadas con el déficit necesario, pero puede modificarlas si ingresaron más unidades.
        </Paragraph>

        <List
          bordered
          dataSource={deficitItems}
          renderItem={(item) => (
            <List.Item style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1, paddingRight: 16 }}>
                <Text strong>{item.item_name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Stock actual: {item.available} UN | Déficit: {item.deficit} UN</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Cant. a Ingestar:</Text>
                <InputNumber
                  min={1}
                  value={itemQuantities[item.element_id] || item.deficit}
                  onChange={(val) => handleQuantityChange(item.element_id, val)}
                  style={{ width: 90 }}
                />
              </div>
            </List.Item>
          )}
        />

        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Observaciones de Regularización:</Text>
          <Input.TextArea
            rows={2}
            value={adjustmentNotes}
            onChange={(e) => setAdjustmentNotes(e.target.value)}
            placeholder="Ingrese notas u observaciones relativas al ajuste..."
          />
        </div>
      </Modal>
    </div>
  )
}

export default ViewStockOverrideList
